import React, { useState } from 'react';

import { NextPage } from 'next';

import { defaultDropdownStyles } from '../../components/Dropdown/constants';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import { ChevronDownIcon } from '../../components/Icons/ChevronDownIcon';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { useUserInfo } from '../../helpers/hooks/useUserInfo';
import { getUserUrl } from '../../helpers/urls';
import { getFullName } from '../../helpers/user/getFullName';
import { GetProfile_user } from '../../interfaces/graphql';

import imagePlaceholder from './assets/imagePlaceholder.svg';
import { AnglerCatchesView } from './components/AnglerCatchesView/AnglerCatchesView';
import { AnglerDetails } from './components/AnglerDetails/AnglerDetails';
import { AnglerPostsView } from './components/AnglerPostsView/AnglerPostsView';
import { AnglerViewOptions, ANGLER_VIEW_OPTIONS } from './constants';
import { getProfilePageSchema } from '../../helpers/schema';

export interface IAnglerProps {
  angler: GetProfile_user;
  blogSlug: string | null;
}

export const Angler: NextPage<IAnglerProps> = ({ angler, blogSlug }) => {
  const { user } = useUserInfo();
  const [sortBy, setSortBy] = useState<AnglerViewOptions['value']>('catches');
  const {
    nickname,
    avatar,
    coverImage,
    countryCode,
    biography,
    _id: anglerId,
    isPremium,
    catches,
  } = angler;

  const checkedAvatarImage = avatar?.url || imagePlaceholder.src;
  const fullName = getFullName(angler);
  const anglerUrl = getUserUrl(nickname);

  const usersOwnPage = (user && user._id === angler._id) || false;
  const currentUserNickname = user ? user.nickname : null;
  const totalAnglerCatches = catches.totalCount;

  return (
    <div className="mx-auto block max-w-screen-lg md:mx-auto md:mt-12 md:flex md:justify-center md:gap-8">
      <MetaTags
        link={anglerUrl}
        title={`${fullName} (@${nickname}) • Angler on Fishbrain`}
        image={checkedAvatarImage}
        schema={getProfilePageSchema({ fullName, nickname, avatarUrl: checkedAvatarImage })}
        description={`@${nickname} logged ${totalAnglerCatches} catch reports on Fishbrain ➡️️️️ Join ${fullName} and begin tracking your latest catches with Fishbrain`}
        robots="noindex, nofollow"
      />

      <AnglerDetails
        coverImage={coverImage}
        avatar={checkedAvatarImage}
        nickname={nickname}
        fullName={fullName}
        countryCode={countryCode}
        biography={biography}
        anglerId={anglerId}
        currentUserNickname={currentUserNickname}
        isPremium={isPremium || false}
        blogSlug={blogSlug}
        totalAnglerCatches={totalAnglerCatches}
      />

      <div className="mx-4 sm:mx-auto sm:w-1/2 md:mx-0">
        <Dropdown
          customLabelComponent={<></>}
          defaultOption={ANGLER_VIEW_OPTIONS.find(({ value }) => value === sortBy)!}
          options={ANGLER_VIEW_OPTIONS}
          onChange={({ value }) => {
            setSortBy(value);
          }}
          dropdownIcon={<ChevronDownIcon />}
          dropdownStyles={{
            ...defaultDropdownStyles,
            previewOptionClasses: 'sm:mr-3 body-text-md !font-heading flex md:sticky md:top-0',
            openedListboxClasses:
              'left-3 block absolute bg-sea-foam z-mid shadow-md mt-0 py-2 rounded-lg outline-none focus:ring-1 focus:border-tuna-shade',
          }}
        />
        {sortBy === 'catches' && (
          <AnglerCatchesView
            usersOwnPage={usersOwnPage}
            nickname={nickname}
            totalAnglerCatches={totalAnglerCatches}
          />
        )}
        {sortBy === 'posts' && (
          <AnglerPostsView
            className="md:mx-12"
            usersOwnPage={usersOwnPage}
            nickname={nickname}
            user={angler}
          />
        )}
      </div>
    </div>
  );
};
